// components/quizPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, CheckCircle, XCircle, Home, RotateCcw } from 'lucide-react';
import { quizQuestions } from '../data/quizQuestions';

const QuizPage = () => {
  const navigate = useNavigate();
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Initialize quiz on component mount
  useEffect(() => {
    const questions = selectRandomQuestions();
    setSelectedQuestions(questions);
  }, []);

  // Randomly select 10 questions for the quiz
  const selectRandomQuestions = () => {
    const shuffled = [...quizQuestions].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(10, quizQuestions.length));
  };

  const handleAnswerSelect = (answerIndex) => {
    if (!showFeedback) {
      setSelectedAnswer(answerIndex);
    }
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer !== null) {
      setShowFeedback(true);
    }
  };

  const handleNextQuestion = () => {
    const newAnswers = [...userAnswers, selectedAnswer];
    setUserAnswers(newAnswers);
    setSelectedAnswer(null);
    setShowFeedback(false);

    if (currentQuestionIndex < selectedQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Quiz is complete - calculate final score and track completion
      const finalScore = newAnswers.reduce((score, answer, index) => {
        return score + (answer === selectedQuestions[index].correctAnswer ? 1 : 0);
      }, 0);
      
      const percentage = Math.round((finalScore / selectedQuestions.length) * 100);
      
      // Track quiz completion event
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'quiz_completed', {
          'quiz_score': finalScore,
          'total_questions': selectedQuestions.length,
          'score_percentage': percentage,
          'performance_category': percentage >= 80 ? 'excellent' : percentage >= 60 ? 'good' : 'needs_improvement'
        });
      }
      
      setShowResults(true);
    }
  };

  const calculateScore = () => {
    return userAnswers.reduce((score, answer, index) => {
      return score + (answer === selectedQuestions[index].correctAnswer ? 1 : 0);
    }, 0);
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const retakeQuiz = () => {
    const questions = selectRandomQuestions();
    setSelectedQuestions(questions);
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setSelectedAnswer(null);
    setShowResults(false);
    setShowFeedback(false);
    
    // Track quiz retake event
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'quiz_retake', {
        'source': 'results_page'
      });
    }
  };

  // Show results screen
  if (showResults) {
    const score = calculateScore();
    const percentage = Math.round((score / selectedQuestions.length) * 100);
    
    return (
      <div className="bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="py-8">
          <div className="max-w-4xl mx-auto px-4">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Quiz Complete!</h2>
                <div className={`text-6xl font-bold ${getScoreColor(score)} mb-2`}>
                  {score}/{selectedQuestions.length}
                </div>
                <div className="text-2xl text-gray-600 mb-6">
                  {percentage}% Correct
                </div>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={retakeQuiz}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Retake Quiz
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <Home className="mr-2 h-4 w-4" />
                    Back to Home
                  </button>
                </div>
              </div>
            </div>

            {/* Detailed Results */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 text-center">Review Your Answers</h3>
              {selectedQuestions.map((question, index) => {
                const userAnswer = userAnswers[index];
                const isCorrect = userAnswer === question.correctAnswer;
                
                return (
                  <div key={index} className="bg-white rounded-2xl shadow-lg p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        {isCorrect ? (
                          <CheckCircle className="h-6 w-6 text-green-500" />
                        ) : (
                          <XCircle className="h-6 w-6 text-red-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">
                          Question {index + 1}: {question.question}
                        </h4>
                        
                        <div className="space-y-2 mb-4">
                          {question.answers.map((answer, answerIndex) => (
                            <div
                              key={answerIndex}
                              className={`p-3 rounded-lg border ${
                                answerIndex === question.correctAnswer
                                  ? 'bg-green-50 border-green-200 text-green-800'
                                  : answerIndex === userAnswer && !isCorrect
                                  ? 'bg-red-50 border-red-200 text-red-800'
                                  : 'bg-gray-50 border-gray-200 text-gray-700'
                              }`}
                            >
                              {answer}
                              {answerIndex === question.correctAnswer && (
                                <span className="ml-2 text-green-600 font-semibold">✓ Correct</span>
                              )}
                              {answerIndex === userAnswer && !isCorrect && (
                                <span className="ml-2 text-red-600 font-semibold">✗ Your Answer</span>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-3">
                          <p className="text-blue-800">{question.explanation}</p>
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          <strong>Sources:</strong> {question.sources.join(', ')}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading if questions haven't loaded yet
  if (selectedQuestions.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="flex items-center justify-center" style={{height: 'calc(100vh - 80px)'}}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading quiz...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show quiz question
  const currentQuestion = selectedQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / selectedQuestions.length) * 100;

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="py-8">
        <div className="max-w-3xl mx-auto px-4">
          {/* Progress Bar */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Question {currentQuestionIndex + 1} of {selectedQuestions.length}
              </h2>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              {currentQuestion.question}
            </h3>
            
            <div className="space-y-4 mb-8">
              {currentQuestion.answers.map((answer, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={showFeedback}
                  className={`w-full p-4 text-left rounded-xl border-2 transition-all duration-200 ${
                    showFeedback
                      ? index === currentQuestion.correctAnswer
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : index === selectedAnswer && selectedAnswer !== currentQuestion.correctAnswer
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 bg-gray-50 text-gray-700'
                      : selectedAnswer === index
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100 text-gray-700 cursor-pointer'
                  } ${showFeedback ? 'cursor-default' : ''}`}
                >
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full border-2 mr-4 flex items-center justify-center ${
                      showFeedback
                        ? index === currentQuestion.correctAnswer
                          ? 'border-green-500 bg-green-500'
                          : index === selectedAnswer && selectedAnswer !== currentQuestion.correctAnswer
                          ? 'border-red-500 bg-red-500'
                          : 'border-gray-300'
                        : selectedAnswer === index
                        ? 'border-indigo-500 bg-indigo-500'
                        : 'border-gray-300'
                    }`}>
                      {showFeedback && index === currentQuestion.correctAnswer && (
                        <CheckCircle className="h-3 w-3 text-white" />
                      )}
                      {showFeedback && index === selectedAnswer && selectedAnswer !== currentQuestion.correctAnswer && (
                        <XCircle className="h-3 w-3 text-white" />
                      )}
                      {!showFeedback && selectedAnswer === index && (
                        <div className="w-full h-full rounded-full bg-white scale-50"></div>
                      )}
                    </div>
                    <span className="text-lg">{answer}</span>
                    {showFeedback && index === currentQuestion.correctAnswer && (
                      <span className="ml-auto text-green-600 font-semibold">✓ Correct</span>
                    )}
                    {showFeedback && index === selectedAnswer && selectedAnswer !== currentQuestion.correctAnswer && (
                      <span className="ml-auto text-red-600 font-semibold">✗ Incorrect</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Feedback Section */}
            {showFeedback && (
              <div className="mb-8">
                <div className={`p-4 rounded-lg border-l-4 ${
                  selectedAnswer === currentQuestion.correctAnswer
                    ? 'bg-green-50 border-green-400' 
                    : 'bg-red-50 border-red-400'
                }`}>
                  <div className="flex items-center mb-2">
                    {selectedAnswer === currentQuestion.correctAnswer ? (
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600 mr-2" />
                    )}
                    <h4 className={`font-semibold ${
                      selectedAnswer === currentQuestion.correctAnswer ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {selectedAnswer === currentQuestion.correctAnswer ? 'Correct!' : 'Incorrect'}
                    </h4>
                  </div>
                  <p className={`${selectedAnswer === currentQuestion.correctAnswer ? 'text-green-700' : 'text-red-700'} mb-3`}>
                    {currentQuestion.explanation}
                  </p>
                  <div className={`text-sm ${selectedAnswer === currentQuestion.correctAnswer ? 'text-green-600' : 'text-red-600'}`}>
                    <strong>Sources:</strong> {currentQuestion.sources.join(', ')}
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-4">
              {!showFeedback ? (
                <button
                  onClick={handleSubmitAnswer}
                  disabled={selectedAnswer === null}
                  className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ${
                    selectedAnswer !== null
                      ? 'text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 cursor-pointer'
                      : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                  }`}
                >
                  Submit Answer
                  <ChevronRight className="ml-2 h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={handleNextQuestion}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                >
                  {currentQuestionIndex === selectedQuestions.length - 1 ? 'Finish Quiz' : 'Next Question'}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;